import { FunctionComponent } from "react";

import { classNames } from "@/utils/classNames";

type OnboardingStepperProps = {
	step: number;
	/**
	 * How many steps the wizard has: four with the checkout (V1), three without
	 * it (V2, PREC-3719) — the dots must not promise a payment screen that the
	 * flow never shows.
	 */
	total?: number;
};

/** Dot colour per step, in flow order. */
const DOT_COLORS = ["bg-brand-light", "bg-[#514FF0]", "bg-[#805FEF]", "bg-[#B771EE]"];

/** Connector gradient between a step and the next one. */
const CONNECTOR_GRADIENTS = [
	"bg-gradient-to-r from-[#1D3DF2] to-[#B771EE]",
	"bg-gradient-to-r from-[#514FF0] to-[#805FEF]",
	"bg-gradient-to-r from-[#805FEF] to-[#B771EE]",
];

/** The first connector animates its fill and turns solid once it is behind us. */
const Connector: FunctionComponent<{ index: number; step: number }> = ({ index, step }) => {
	const isFirst = index === 1;
	const isCurrent = step === index;
	const isDone = step > index;

	return (
		<span
			className={classNames(
				"w-[42px] h-[4px] rounded-md bg-border-light",
				isFirst && "flex animate-background"
			)}
		>
			{(isCurrent || isDone) && (
				<span
					className={classNames(
						"flex h-[4px] rounded-md",
						isFirst && "animate-width",
						isCurrent ? "w-1/2" : "w-full",
						isDone && isFirst ? "bg-blue-action" : CONNECTOR_GRADIENTS[index - 1]
					)}
				></span>
			)}
		</span>
	);
};

export const OnboardingStepper: FunctionComponent<OnboardingStepperProps> = ({
	step,
	total = 4,
}: OnboardingStepperProps) => {
	const steps = Array.from({ length: total }, (_, index) => index + 1);

	return (
		<div className="flex flex-row gap-2 items-center">
			{steps.map((current) => (
				<span className="flex flex-row gap-2 items-center" key={current}>
					{current > 1 && <Connector index={current - 1} step={step} />}
					<span
						className={classNames(
							"flex items-center justify-center text-xs font-bold rounded-full w-5 h-5",
							step >= current
								? `${DOT_COLORS[current - 1]} text-white`
								: "text-text-meta bg-border-light"
						)}
					>
						{current}
					</span>
				</span>
			))}
		</div>
	);
};
